import React from 'react'
import {
  Map,
  fromJS,
  List,
} from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import {
  GUIDE_OVERVIEW_TAB_VALUE,
  SERIES_TAB_VALUE,
  DETAIL_PAGE_STATIC_KEY,
} from '../../../services/detail/index'
import {
  isGuide,
  isEpisode,
  getLocationType,
  isSubtypeYogaFitnessMeditation,
  getAudioLanguages,
  getSubtitleCaptionLanguages,
} from '../../../services/content/index'
import {
  TYPE_CONTENT_SERIES,
} from '../../../services/content-type/index'
import { hasLiveAccessEntitlement } from '../../../services/subscription/index'
import ReadMoreLess from '../../ReadMoreLess/index'
import LabeledField from '../../LabeledField/index'
import Vote, { SIZE_MEDIUM } from '../../Vote/index'
import FileAttachmentsGraphQL from '../../FileAttachmentsGraphQL/index'
import DetailActionButtons from '../DetailActionButtons'
import DetailContentLayoutTemplate from '../DetailContentLayoutTemplate'
import { getDetailPageUiLocationGQL } from '../utils'

/**
 * Series Seasons Count
 * @param {Object} props - The props
 * @param {Integer} props.totalSeasons - total seasons for the current series
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} - Series season count
 */
function SeriesSeasonsCount (props) {
  const { totalSeasons, staticText } = props
  const label = totalSeasons > 1 ? staticText.get('seasons') : staticText.get('season')

  return (
    <div className="detail-page__content-meta-series-count">
      {totalSeasons} {label}
    </div>
  )
}

function DetailSeries (props) {
  const {
    auth,
    isGuideDetailPage,
    isEpisodeDetailPage,
    tabData,
    staticText,
  } = props
  const contentType = tabData.get('contentType')
  const audioLanguages = getAudioLanguages({ data: tabData, contentType })
  const subtitleCaptionLanguages = getSubtitleCaptionLanguages({ data: tabData, contentType })
  const detailLocationType = getLocationType({ data: tabData })
  let description = tabData.get('description')
  let host = tabData.get('instructor')
  let guest = tabData.get('cast')
  let totalSeasons = tabData.get('seasonsCount')
  let isYogaFitnessMeditation = isSubtypeYogaFitnessMeditation({
    contentSubtype: tabData.get('contentSubtype'),
  })
  let vote = tabData.get('vote') || Map()
  let id = tabData.get('contentId')
  let title = tabData.get('title')
  let fileAttachmentData = tabData.get('assets') || List()

  if (isEpisodeDetailPage) {
    description = tabData.get('seriesDescription', '')
    host = tabData.get('seriesInstructor')
    guest = tabData.get('seriesCast')
    totalSeasons = tabData.get('seriesSeasonsCount')
    vote = tabData.get('seriesVote') || Map()
    id = tabData.get('seriesId')
    title = tabData.get('seriesTitle')
    isYogaFitnessMeditation = isSubtypeYogaFitnessMeditation({
      contentSubtype: tabData.get('seriesContentSubtype'),
    })
    fileAttachmentData = tabData.get('seriesAssets') || List()
  }

  const ga4EventData = fromJS({
    content_id: id,
    content_type: TYPE_CONTENT_SERIES,
    content_name: title,
    ui_location: getDetailPageUiLocationGQL(detailLocationType),
  })

  return (
    <DetailContentLayoutTemplate
      rowOne={
        <DetailActionButtons
          activeTabValue={isGuideDetailPage ? GUIDE_OVERVIEW_TAB_VALUE : SERIES_TAB_VALUE}
          tabData={tabData}
          isInlinePlaybackActive={props.isInlinePlaybackActive}
        />
      }
      rowTwo={
        <>
          <ReadMoreLess text={description} maxLength={700} />
        </>
      }
      rowThree={
        <div className="detail-page__content-meta-info">
          <LabeledField
            value={host}
            label={isYogaFitnessMeditation ? staticText.get('teacher') : staticText.get('host')}
          />
          {
            isGuideDetailPage && <LabeledField
              value={guest}
              label={staticText.get('featuredSpeakers')}
            />
          }
          {
            audioLanguages
              ? <LabeledField
                  value={audioLanguages}
                  label={staticText.get('audio')}
                />
              : null
          }
          {
            subtitleCaptionLanguages
              ? <LabeledField
                  value={subtitleCaptionLanguages}
                  label={staticText.get('subtitles')}
                />
              : null
          }
        </div>
      }
      rowFour={
        !isGuideDetailPage
          ? <div className="detail-page__content-meta-inline">
              <SeriesSeasonsCount totalSeasons={totalSeasons} staticText={staticText} />
              <Vote
                className="detail-page__content-meta-vote"
                size={SIZE_MEDIUM}
                vote={vote.get('upVote')}
                voteDown={vote.get('downVote')}
                voteId={id}
                auth={auth}
                ga4EventData={ga4EventData}
                userVote={tabData.get('seriesUserVote')}
              />
            </div>
          : null
      }
      rowFive={
        <FileAttachmentsGraphQL
          data={fileAttachmentData}
          sectionTitle={staticText.get('bonusMaterials')}
        />
      }
    />
  )
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
      } = state
      const { tabData = Map() } = props
      const contentType = tabData.get('contentType')
      const contentSubtype = tabData.get('contentSubtype')
      const displayType = tabData.get('displayType')
      const data = tabData
      const subscriptions = auth.get('subscriptions')
      const hasLiveAccess = hasLiveAccessEntitlement(subscriptions)

      return {
        auth,
        staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
        isEpisodeDetailPage: isEpisode({ contentType, data }),
        isGuideDetailPage: isGuide({ contentSubtype, displayType }),
        hasLiveAccess,
      }
    },
  ),
)(DetailSeries)
